/**
 * First-visit intro-sheet gating ("Welcome to Trash Trail").
 *
 * The intro auto-opens ONCE per browser, ~800ms after the first
 * manifest load (so the map paints behind it), and only when:
 *
 *   1. the localStorage flag `trail.intro_seen` is unset (any dismissal
 *      writes it — backdrop tap, ✕, or the primary button), AND
 *   2. the client is NOT automation (`navigator.webdriver`) — keeps the
 *      sheet out of every Playwright e2e + screenshot-harness flow.
 *
 * `?intro=1` (or bare `?intro`) FORCES the sheet open for QA,
 * overriding both gates. Mirrors the readShowUnverified /
 * writeShowUnverified localStorage pattern in explore.tsx.
 */

export const STORAGE_KEY_INTRO_SEEN = 'trail.intro_seen';

/** Delay between first manifest load and the intro auto-open. */
export const INTRO_AUTO_OPEN_DELAY_MS = 800;

export function readIntroSeen(): boolean {
  if (typeof window === 'undefined') return true; // no storage → never auto-open
  return window.localStorage.getItem(STORAGE_KEY_INTRO_SEEN) === 'true';
}

export function writeIntroSeen(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY_INTRO_SEEN, 'true');
}

/** `?intro=1` (or bare `?intro`) forces the sheet for QA; `?intro=0` doesn't. */
export function introForcedByUrl(search: string): boolean {
  const v = new URLSearchParams(search).get('intro');
  return v !== null && v !== '0';
}

/**
 * The single auto-open decision. Pure — callers pass the environment in
 * so the rule is unit-testable without a browser.
 */
export function shouldAutoOpenIntro(opts: {
  /** window.location.search (leading "?" optional). */
  search: string;
  /** navigator.webdriver === true (Playwright, screenshot harnesses). */
  webdriver: boolean;
  /** readIntroSeen() — the one-time localStorage flag. */
  seen: boolean;
}): boolean {
  if (introForcedByUrl(opts.search)) return true;
  return !opts.seen && !opts.webdriver;
}
